import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { SegmentSetOpsModalService, SegmentSetOpsModalState } from '../services/segment-set-ops-modal.service';
import { SegmentService } from '../services/segment-api.service';
import { ToastService } from '../../../shared/services/toast.service';
import { Segment } from '../models/segment.model';

@Component({
  selector: 'app-segment-set-ops-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './segment-set-ops-modal.component.html',
})
export class SegmentSetOpsModalComponent implements OnInit, OnDestroy {
  state: SegmentSetOpsModalState = { isOpen: false, segmentId: null };

  setOpType: 'UNION' | 'INTERSECT' | 'DIFFERENCE' = 'UNION';
  availableSegments: Segment[] = [];
  selectedSourceIds: number[] = [];
  baseSegmentId: number | null = null;
  subtractIds: number[] = [];
  isExecuting = false;
  isLoadingSegments = false;

  readonly opTypes = [
    { value: 'UNION' as const, label: 'Unión', sub: 'A + B' },
    { value: 'INTERSECT' as const, label: 'Intersección', sub: 'A ∩ B' },
    { value: 'DIFFERENCE' as const, label: 'Diferencia', sub: 'A - B' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private modalSvc: SegmentSetOpsModalService,
    private segmentApi: SegmentService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.modalSvc.state$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      const justOpened = !this.state.isOpen && s.isOpen;
      this.state = s;
      if (justOpened && s.segmentId) {
        this.reset();
        this.loadSegments(s.segmentId);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.setOpType = 'UNION';
    this.selectedSourceIds = [];
    this.baseSegmentId = null;
    this.subtractIds = [];
    this.isExecuting = false;
    this.availableSegments = [];
  }

  onOpTypeChange(type: 'UNION' | 'INTERSECT' | 'DIFFERENCE'): void {
    this.setOpType = type;
    this.selectedSourceIds = [];
    this.baseSegmentId = null;
    this.subtractIds = [];
  }

  toggleBase(id: number): void {
    this.baseSegmentId = this.baseSegmentId === id ? null : id;
    this.subtractIds = this.subtractIds.filter(s => s !== id);
  }

  toggleSubtract(id: number): void {
    const idx = this.subtractIds.indexOf(id);
    if (idx === -1) this.subtractIds.push(id);
    else this.subtractIds.splice(idx, 1);
  }

  get canExecute(): boolean {
    if (this.setOpType === 'DIFFERENCE') return !!this.baseSegmentId && this.subtractIds.length > 0;
    return this.selectedSourceIds.length > 0;
  }

  private loadSegments(excludeId: number): void {
    this.isLoadingSegments = true;
    this.segmentApi.getSegments(0, 100, { status: 'READY' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.availableSegments = (res.content || []).filter(s => s.id !== excludeId);
          this.isLoadingSegments = false;
        },
        error: () => {
          this.isLoadingSegments = false;
          this.toast.error({ title: 'Error', message: 'No se pudieron cargar los segmentos disponibles.' });
        },
      });
  }

  toggleSource(id: number): void {
    const idx = this.selectedSourceIds.indexOf(id);
    if (idx === -1) this.selectedSourceIds.push(id);
    else this.selectedSourceIds.splice(idx, 1);
  }

  isSelected(id: number): boolean {
    return this.selectedSourceIds.includes(id);
  }

  ejecutar(): void {
    if (!this.state.segmentId || !this.canExecute || this.isExecuting) return;
    const sourceSegmentIds = this.setOpType === 'DIFFERENCE'
      ? [this.baseSegmentId!, ...this.subtractIds]
      : this.selectedSourceIds;
    this.isExecuting = true;
    this.segmentApi.executeSetOperation(this.state.segmentId, {
      sourceSegmentIds,
      operationType: this.setOpType,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isExecuting = false;
        this.toast.success({ title: 'Operación ejecutada', message: 'Los miembros del segmento fueron actualizados.' });
        this.modalSvc.notifyExecuted(this.state.segmentId!);
        this.close();
      },
      error: () => {
        this.isExecuting = false;
        this.toast.error({ title: 'Error', message: 'No se pudo ejecutar la operación de conjuntos.' });
      },
    });
  }

  close(): void {
    this.modalSvc.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.state.isOpen) this.close();
  }
}
